import { useApolloClient } from '@apollo/client';
import {
  Box,
  Button,
  Center,
  Progress,
  Flex,
  Heading,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { Formik, Form, FormikProps } from 'formik';
import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useDropzone, FileWithPath } from 'react-dropzone';
import { v4 as uuidV4 } from 'uuid';

import {
  finleyAssert,
  error as commonError,
} from '@finley-technologies/common';
import { Deliverable } from '@finley-technologies/finley-types';

import {
  DeliverableFragmentGQLType,
  DeliverableValueTypeGQLType,
  useSubmitDeliverableSubmissionMutation,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { colors } from 'packages/common-frontend/colors';
import { InputFormField } from 'packages/common-frontend/components/FormField';
import {
  errorToastConfig,
  successToastConfig,
} from 'packages/common-frontend/constants';
import {
  getAcceptedFileTypesConfig,
  truncateUploadFileName,
} from 'packages/common-frontend/utils';
import { GET_DELIVERABLE } from 'packages/deliverables/requests/deliverables.gql';
import { GET_DELIVERABLE_SUBMISSION_SIGNED_URL } from 'packages/files/requests/files.gql';
import { presignedPostUpload } from 'packages/files/ui/utils';
import { FiFileMinus, FiFilePlus } from 'react-icons/fi';

export const ValueInput: React.FC<{
  formProps: FormikProps<{
    deliverableId: string;
    value: string;
    valueType: DeliverableValueTypeGQLType;
  }>;
  label?: string;
  valueType: DeliverableValueTypeGQLType;
}> = ({ valueType, formProps, label = 'Value' }) => {
  switch (valueType) {
    case 'string':
      return (
        <InputFormField
          type="text"
          propName="value"
          label={label}
          formProps={formProps}
          labelWidth="sm"
          inputWidth="sm"
          flexDirection="row"
          alignItems="flex-end"
        />
      );
    case 'number':
      return (
        <InputFormField
          type="number"
          propName="value"
          label={label}
          formProps={formProps}
          labelWidth="sm"
          inputWidth="sm"
          flexDirection="row"
          alignItems="flex-end"
        />
      );
    case 'boolean':
      // not sure how boolean will be used yet
      return (
        <InputFormField
          type="text"
          propName="value"
          label={label}
          formProps={formProps}
          labelWidth="sm"
          inputWidth="sm"
          flexDirection="row"
          alignItems="flex-end"
        />
      );
    case 'file':
    default:
      throw new Error(`Unknown value type: ${valueType}`);
  }
};

export const SubmitFileSubmissionDropzone: React.FC<{
  setFieldValue: FormikProps<{
    deliverableId: string;
    value: string;
    valueType: Deliverable.v3DEPRECATED.DeliverableValueType;
  }>['setFieldValue'];
  setRejected: React.Dispatch<React.SetStateAction<boolean>>;
  accept: any;
  uploadProgress: number | null;
}> = ({ accept, uploadProgress, setFieldValue, setRejected }) => {
  const loading = _.isNumber(uploadProgress);
  const {
    acceptedFiles,
    fileRejections,
    getRootProps,
    getInputProps,
  } = useDropzone({
    accept,
    multiple: false,
    onDrop: useCallback(
      (file) => {
        setFieldValue('file', file[0]);
      },
      [setFieldValue],
    ),
    disabled: loading,
  });

  useEffect(() => {
    if (!_.isEmpty(fileRejections)) {
      setRejected(true);
    } else {
      setRejected(false);
    }
  }, [setRejected, fileRejections]);

  const acceptedFileItems = _.map(acceptedFiles, (file: FileWithPath) => (
    <Text key={file.path} textAlign="center" mt="10px">
      {truncateUploadFileName(file.path)}
    </Text>
  ));

  const fileRejectionItems = _.map(
    fileRejections,
    ({ file }: { file: FileWithPath }) => {
      const acceptedFileTypes = _.flatMap(accept, (values) =>
        _.map(values, (value) => value.slice(1)),
      );

      return (
        <>
          <Text key={file.path} textAlign="center" mt="10px">
            {truncateUploadFileName(file.path)}
          </Text>
          <Text textAlign="center" mt="10px">
            Expected file of type{' '}
            {_.map(acceptedFileTypes, (type, index) => (
              <em key={type}>{`${
                index !== acceptedFileTypes.length - 1
                  ? `${type}, `
                  : `or ${type}`
              }`}</em>
            ))}
          </Text>
        </>
      );
    },
  );
  const fileSelected = !_.isEmpty(acceptedFiles) || !_.isEmpty(fileRejections);

  return (
    <Center
      border="1px"
      borderRadius="md"
      cursor={loading ? 'wait' : 'pointer'}
      minHeight="200px"
      flexDirection="column"
      width="100%"
      {...getRootProps({ className: 'dropzone' })}
    >
      <input {...getInputProps()} />
      {!_.isEmpty(acceptedFiles) && (
        <>
          {loading && uploadProgress === 100 && (
            <Heading size="sm">
              <Icon color="green.300" fontSize="lg">
                <FiFilePlus />
              </Icon>{' '}
              Accepted
            </Heading>
          )}
          {loading && uploadProgress < 100 && (
            <Heading size="sm">
              <Icon color="grey" fontSize="lg">
                <FiFilePlus />
              </Icon>{' '}
              Uploading Files. Please do not refresh page.
            </Heading>
          )}
          {loading && (
            <Box minWidth="150px" width="50%" m="10px 0">
              <Progress
                value={uploadProgress}
                borderRadius="md"
                sx={{
                  '& > div': {
                    background: colors.green[300],
                  },
                }}
              />
            </Box>
          )}
          {!loading && (
            <Flex alignItems="center">
              <Icon color="green.300" fontSize="lg">
                <FiFilePlus />
              </Icon>
              <Heading size="sm">Valid File Type</Heading>
            </Flex>
          )}
          {acceptedFileItems}
        </>
      )}
      {!_.isEmpty(fileRejections) && (
        <>
          <Flex alignItems="center">
            <Icon color="red.300" fontSize="lg">
              <FiFileMinus />
            </Icon>
            <Heading size="sm">Invalid File Type</Heading>
          </Flex>
          {fileRejectionItems}
        </>
      )}
      {!loading && (
        <Text
          textAlign="center"
          as={fileSelected ? 'sub' : 'i'}
          pt={fileSelected ? '20px' : '0'}
        >
          {`Drag a file here, or click to select${
            fileSelected ? ' a new' : ''
          } file`}
        </Text>
      )}
    </Center>
  );
};

export const SubmitFileSubmission: React.FC<{
  deliverable: DeliverableFragmentGQLType;
  submissions: { value: string }[];
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onCompleteSubmit?: () => void;
}> = ({ deliverable, isOpen, submissions, setIsOpen, onCompleteSubmit }) => {
  const toast = useToast();
  const client = useApolloClient();
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const loading = _.isNumber(uploadProgress);
  const [rejected, setRejected] = useState<boolean>(false);

  useEffect(() => {
    const browserNavigationHandle = () =>
      window.history.pushState(null, '', document.URL);

    if (_.isNumber(uploadProgress)) {
      window.history.pushState(null, '', document.URL);
      window.addEventListener('popstate', browserNavigationHandle);
      document.body.style.cursor = 'wait';
    }
    return () => {
      window.removeEventListener('popstate', browserNavigationHandle);
      document.body.style.cursor = 'default';
    };
  }, [uploadProgress]);

  finleyAssert(
    deliverable &&
      deliverable.expectedValueConfig.__typename ===
        'DeliverableExpectedValueConfigFile',
    new commonError.BadRequest({
      message:
        'User attempting to submit a file submission for a non-file deliverable',
      externalMessage: 'Deliverable is not a file submission',
      metadata: {
        logMeta: {
          deliverableId: deliverable.id,
          deliverableName: deliverable.name,
          creditFacilitySlug: deliverable.creditFacilitySlug,
        },
      },
    }),
  );

  const fileTypes = deliverable.expectedValueConfig.fileTypes;

  const [submitDeliverableSubmission] = useSubmitDeliverableSubmissionMutation({
    onCompleted: () => {
      toast({
        ...successToastConfig,
        title: 'Successfully submitted deliverable',
        id: uuidV4(),
      });
      setIsOpen(false);
    },
    onError: (data: any) => {
      Sentry.captureException(data);
      errorToastConfig.title = data.message;
      toast(errorToastConfig);
    },
  });

  const pastFileNames = _.map(submissions, ({ value }) =>
    value.split('/').pop(),
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (!loading) {
          setIsOpen(false);
        }
      }}
    >
      <ModalOverlay />
      <ModalContent padding="20px" minW="5xl" width="2xl">
        <ModalHeader>{`${deliverable.name} Submission`}</ModalHeader>
        <ModalCloseButton isDisabled={loading} />
        <ModalBody width="100%">
          <Formik
            onSubmit={async ({ file }: { file: FileWithPath | null }) => {
              try {
                if (file) {
                  if (_.includes(pastFileNames, file.name)) {
                    throw new Error(
                      `Unable to upload submission with filename [${file.path}] as it already exists`,
                    );
                  }
                  const { s3FilePath } = await presignedPostUpload<
                    {
                      creditFacilitySlug: string;
                      deliverableId: string;
                      deliverableName: string;
                      fileName?: string;
                    },
                    {}
                  >({
                    client,
                    file,
                    query: GET_DELIVERABLE_SUBMISSION_SIGNED_URL,
                    queryVariables: {
                      creditFacilitySlug: deliverable.creditFacilitySlug,
                      deliverableId: deliverable.id,
                      deliverableName: deliverable.name,
                      fileName: file.path,
                    },
                    progressEventCallback: ({ loaded, total }) => {
                      const percentCompleted = Math.ceil(
                        (loaded * 100) / total,
                      );
                      setUploadProgress(percentCompleted);
                    },
                  });

                  await submitDeliverableSubmission({
                    variables: {
                      id: uuidV4(),
                      deliverableId: deliverable.id,
                      value: s3FilePath, // TODO does it return the bucvket + key it was uploaded to?
                      valueType: 'file',
                    },
                  });
                  // reset upload progress for next upload

                  setUploadProgress(null);

                  if (onCompleteSubmit) {
                    onCompleteSubmit();
                  }
                }
              } catch (err: any) {
                Sentry.captureException(err);
                let message = 'Error uploading deliverable';
                if (
                  err?.message &&
                  err.message.includes(
                    'Unable to upload submission with filename',
                  )
                ) {
                  message = err.message;
                }
                toast({
                  ...errorToastConfig,
                  id: uuidV4(),
                  title: message,
                });
                // reset upload progress for next upload
                setUploadProgress(null);
              }
            }}
            initialValues={{
              file: null,
            }}
            validateOnChange={false}
          >
            {({ setFieldValue, handleSubmit }) => (
              <Form onSubmit={handleSubmit}>
                <VStack spacing="10px">
                  <SubmitFileSubmissionDropzone
                    setFieldValue={setFieldValue}
                    accept={getAcceptedFileTypesConfig(fileTypes)}
                    uploadProgress={uploadProgress}
                    setRejected={setRejected}
                  />
                  <Flex justifyContent="center" mt="8">
                    <Button
                      type="submit"
                      aria-label="submit"
                      isLoading={_.isNumber(uploadProgress)}
                      isDisabled={rejected}
                    >
                      Submit
                    </Button>
                  </Flex>
                </VStack>
              </Form>
            )}
          </Formik>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export const SubmitNonFileSubmissionModal: React.FC<{
  deliverable: DeliverableFragmentGQLType;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ deliverable, isOpen, setIsOpen }) => {
  const toast = useToast();
  // submit deliverable submission mutation hook
  const [submitDeliverableSubmission] = useSubmitDeliverableSubmissionMutation({
    onCompleted: () => {
      if (!toast.isActive(successToastConfig.id)) {
        successToastConfig.title = 'Successfully submitted deliverable';
        toast(successToastConfig);
      }
    },
    onError: (data: any) => {
      Sentry.captureException(data);
      errorToastConfig.title = data.message;
      toast(errorToastConfig);
    },
    refetchQueries: [
      {
        query: GET_DELIVERABLE,
        variables: { deliverableId: deliverable.id },
      },
    ],
  });

  return (
    <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} size="2xl">
      <ModalOverlay />
      <ModalContent padding="20px">
        <ModalHeader>{`Submit ${deliverable.name}`}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Formik<{
            deliverableId: string;
            value: string;
            valueType: DeliverableValueTypeGQLType;
          }>
            onSubmit={(props) => {
              submitDeliverableSubmission({
                variables: { ...props, value: `${props.value}`, id: uuidV4() }, // value expected to be a string
              });
              setIsOpen(false);
            }}
            initialValues={{
              deliverableId: deliverable.id,
              value: '',
              valueType: deliverable.expectedValueConfig.valueType,
            }}
          >
            {(localFormProps) => (
              <Form onSubmit={localFormProps.handleSubmit}>
                <VStack spacing={5}>
                  <ValueInput
                    formProps={localFormProps}
                    valueType={deliverable.expectedValueConfig.valueType}
                  />
                </VStack>
                <Flex justifyContent="center" mt="8">
                  <Button
                    type="submit"
                    aria-label="Done"
                    disabled={_.keys(localFormProps.errors).length > 0}
                  >
                    Done
                  </Button>
                </Flex>
              </Form>
            )}
          </Formik>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export const SubmitDeliverableSubmissionModal: React.FC<{
  deliverable: DeliverableFragmentGQLType;
  submissions: { value: string }[];
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onCompleteSubmit?: () => void;
}> = ({ deliverable, isOpen, setIsOpen, onCompleteSubmit, submissions }) => {
  if (deliverable.expectedValueConfig.valueType === 'file') {
    return (
      <SubmitFileSubmission
        deliverable={deliverable}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        onCompleteSubmit={onCompleteSubmit}
        submissions={submissions}
      />
    );
  }

  return (
    <SubmitNonFileSubmissionModal
      deliverable={deliverable}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
    />
  );
};
